import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Badge, BadgeProps, Divider, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import StyledButton from '../Common/StyledButton';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/rootReducer';
import StyledBackButton from '../Common/StyledBackButton';
import CustomDropzone from './CustomDropzone';
import { onboardingActions } from '../../redux/onboarding/onboardingSlice';
import { therapyTypes } from '../../types/coach';
import { StyledCheckbox } from './Details';

export const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: 10,
    top: 10,
  },
}));

export default function UploadProfilePicture({
  handleBack,
  handleNext,
}: {
  handleBack: () => void;
  handleNext: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const coach = useAppSelector((state: RootState) => state.coach.coach);
  const coachProPic = useAppSelector((state: RootState) => state.coach.coach.profilePic);
  const therapist = useAppSelector((state: RootState) => state.onboarding.therapist);
  const [selectedAvatar, setSelectedAvatar] = useState<number | null>(null);
  const [socialMediaPicture, setSocialMediaPicture] = useState('');
  const [isAgreedPrivacyPolicy, setIsAgreedPrivacyPolicy] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsLetter] = useState(true);
  const [error, setError] = useState(false);
  const cloudfrontURL = process.env.REACT_APP_MEDIA_FILES_BASE_URL;

  useEffect(() => {
    if (!['', null, 'null'].includes(coachProPic)) {
      setSocialMediaPicture(coachProPic);
    }
  }, []);

  const handleOnClickAvatar = (index: number) => {
    setSelectedAvatar(index === selectedAvatar ? null : index);
    dispatch(onboardingActions.setProfilePic(null));
  };

  const onDropAccepted = useCallback((files: File[]) => {
    const file = files[0];
    dispatch(onboardingActions.setProfilePic(file));
    setSelectedAvatar(null);
  }, []);

  const handleDeleteFiles = () => {
    if (socialMediaPicture) {
      setSocialMediaPicture('');
    }
    dispatch(onboardingActions.setProfilePic(null));
  };

  const handleOnClickPrivacyPolicy = () => {
    if (!isAgreedPrivacyPolicy) setError(false);
    setIsAgreedPrivacyPolicy(!isAgreedPrivacyPolicy);
  };

  const handleCreatAccount = () => {
    if (!isAgreedPrivacyPolicy && !coach.isAgreedPrivacyPolicy) {
      setError(true);
      return;
    }
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const coachTypes = therapist.coachTypes.map((coachType) => {
      const index = therapyTypes.findIndex((v) => v.type === coachType);

      const description = therapyTypes[index].values.filter((v) =>
        therapist.therapyTypes.includes(v)
      );
      return { coachType, description: description.map((v) => v.split('.')[2]) };
    });

    let profilePic = '';
    let isExternalImage = false;
    if (socialMediaPicture) {
      profilePic = socialMediaPicture;
      isExternalImage = true;
    } else if (selectedAvatar !== null && selectedAvatar !== undefined) {
      profilePic = `media/common-assets/avatar/avatar-${selectedAvatar + 1}.png`;
    }
    dispatch(
      onboardingActions.createCoachAccount({
        data: {
          CoachOnboarding: {
            id: coach.id,
            timeZone,
            coachTypes,
            profilePic,
            isAgreedPrivacyPolicy: coach.isAgreedPrivacyPolicy
              ? coach.isAgreedPrivacyPolicy
              : isAgreedPrivacyPolicy,
            subscribeToNewsletter: coach.isAgreedPrivacyPolicy
              ? coach.subscribeToNewsletter
              : subscribeToNewsletter,
            isExternalImage,
          },
          profilePic: therapist.profilePic,
        },
      })
    );
    handleNext();
  };

  return (
    <Grid container flexDirection="column" alignItems="center" py={4}>
      <Grid item textAlign="center" xs={12} md={7.5} lg={6.5}>
        <Typography
          variant="title1Bold"
          color="primary.main"
          sx={{ fontSize: { xs: '20px', md: '28px' } }}
        >
          {t('therapistOnboarding.uploadProPic.title')}
        </Typography>
      </Grid>
      <Grid item pt={6} pb={8} sx={{ cursor: 'pointer' }}>
        <CustomDropzone
          accept={{
            'image/jpeg': [],
            'image/png': [],
            'image/gif': [],
            'image/jpg': [],
          }}
          dragInactiveText={
            <>
              <img src="/assets/icons/upload.svg" alt="upload-img" />
              <Typography variant="bodyRegular" color="#7CAEBE" textAlign="center">
                {t('therapistOnboarding.uploadProPic.dragAndDrop')}
              </Typography>
            </>
          }
          dargActiveText={
            <Typography variant="bodyRegular" color="#7CAEBE" textAlign="center">
              {t('therapistOnboarding.uploadProPic.drop')}
            </Typography>
          }
          maxFiles={1}
          invalidFileTypeError={t('therapistOnboarding.uploadProPic.invalidFormat')}
          acceptedFile={
            // eslint-disable-next-line no-nested-ternary
            socialMediaPicture !== ''
              ? socialMediaPicture
              : therapist.profilePic !== null
              ? URL.createObjectURL(therapist.profilePic as File)
              : ''
          }
          handleDelteFiles={handleDeleteFiles}
          onDropAccepted={onDropAccepted}
          maxSize={10 * 1024 * 1024}
        />
      </Grid>
      {socialMediaPicture === '' && therapist.profilePic === null && (
        <Grid item container pt={6} justifyContent="space-between" alignItems="center">
          <Divider sx={{ flexGrow: 1, backgroundColor: '#7CAEBE' }} />
          <Typography variant="bodyRegular" color="#7CAEBE" fontSize="15px" px={1}>
            {t('therapistOnboarding.uploadProPic.selectAvatar')}
          </Typography>
          <Divider sx={{ flexGrow: 1, backgroundColor: '#7CAEBE' }} />
        </Grid>
      )}
      {socialMediaPicture === '' && therapist.profilePic === null && (
        <Grid item container spacing={3} pt={6} pb={6} justifyContent="center">
          {Array.from({ length: 9 }, (_, index) => (
            <Grid
              item
              onClick={() => {
                handleOnClickAvatar(index);
              }}
              sx={{ cursor: 'pointer' }}
            >
              {index === selectedAvatar ? (
                <StyledBadge
                  badgeContent={
                    <Box display="flex" borderRadius="100%" sx={{ backgroundColor: 'white' }}>
                      <img src="/assets/icons/selected-avatar.svg" alt="tick" />
                    </Box>
                  }
                >
                  <Box
                    display="flex"
                    borderRadius="100%"
                    padding="2.5px"
                    sx={{
                      background: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
                    }}
                  >
                    <img
                      src={`${cloudfrontURL}/media/common-assets/avatar/avatar-${index + 1}.png`}
                      alt={`avatar-${index + 1}`}
                    />
                  </Box>
                </StyledBadge>
              ) : (
                <img
                  src={`${cloudfrontURL}/media/common-assets/avatar/avatar-${index + 1}.png`}
                  alt={`avatar-${index + 1}`}
                />
              )}
            </Grid>
          ))}
        </Grid>
      )}

      {!coach.isAgreedPrivacyPolicy && (
        <Grid container pb={2} spacing={{ xs: 2, md: 4 }} justifyContent="center">
          <Grid item xs={12} md={6} textAlign="center">
            <StyledCheckbox
              name="isAgreedPrivacyPolicy"
              checked={isAgreedPrivacyPolicy}
              onChange={handleOnClickPrivacyPolicy}
              inputProps={{ 'aria-label': 'controlled' }}
              checkedIcon={<CheckBoxOutlinedIcon />}
            />
            <>
              <Typography variant="tinyTexts">
                {t('therapistOnboarding.privacyPolicyLabel')}
              </Typography>
              <Typography
                variant="tinyTexts"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => {
                  window.open('https://www.progressmagic.com/privacy-policy');
                }}
              >
                {t('therapistOnboarding.privacyPolicy')}
              </Typography>
            </>
          </Grid>
          <Grid item xs={12} md={6} textAlign="center">
            <StyledCheckbox
              name="subscribeToNewsletter"
              checked={subscribeToNewsletter}
              onChange={() => setSubscribeToNewsLetter(!subscribeToNewsletter)}
              inputProps={{ 'aria-label': 'controlled' }}
              checkedIcon={<CheckBoxOutlinedIcon />}
            />
            <Typography variant="tinyTexts">
              {t('therapistOnboarding.subscribeToNewsLetter')}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid item container justifyContent="space-between">
        <StyledBackButton onClick={handleBack}>{t('therapistOnboarding.back')}</StyledBackButton>
        <StyledButton
          disabled={
            selectedAvatar === null && therapist.profilePic === null && socialMediaPicture === ''
          }
          onClick={handleCreatAccount}
        >
          {t('therapistOnboarding.next')}
        </StyledButton>
      </Grid>
      {error && (
        <Typography
          sx={{
            background: 'linear-gradient(175.46deg, #FF9A9A -9.81%, #FF5858 96.32%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
          variant="bodyBold"
          textAlign="center"
          mt={1}
        >
          {t('therapistOnboarding.validations.privacyPolicy')}
        </Typography>
      )}
    </Grid>
  );
}
